/* eslint-disable no-useless-computed-key */
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import { makeStyles } from "@material-ui/core/styles";
import { Typography, Tooltip } from "@material-ui/core";
import {formatCustomerType, formatLongDate, formatSourceOfFunds} from "../../utils/formatter";
import ModalConsumer from "../consumer/modalConsumer";
import ActionButton from "../button/actionButton";
import UpdateCustomerDrawer from "../drawer/updateCustomerDrawer";
import { OpsRole} from "../../utils/role";
import CopyIcon from "../icons/copyIcon";
import { useState } from "react";

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        marginBottom: '24px',
        backgroundColor: theme.surface.secondary,
    },
    content: {
        width: '100%',
        padding: '24px'
    },
    header: {
        fontSize: '20px',
        lineHeight: '26px',
        fontWeight: 700,
        color: '#FFFFFF',
    },
    iconSection: {
        backgroundColor: 'rgb(255 255 255 / 10%)',
        width: '53px',
        height: '53px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: '33px',
    },
    detailsSection: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
    },
    rowContainer: {
        marginBottom: '32px',
        '&:last-child': {
            marginBottom: '12px'
        },
        '& > h6': {
            fontWeight: 400,
            color: '#FFFFFF',
            opacity: 0.5,
            marginBottom: '8px'
        },
        '& > p, & > div > p': {
            fontWeight: 400,
            color: '#FFFFFF',
            fontSize: '16px',
            lineHeight: '21px',
            margin: 0
        }
    },
    valueWithCopy: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
    },
    copyButton: {
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        padding: '4px',
        display: 'flex',
        alignItems: 'center',
        opacity: 0.7,
        transition: 'opacity 0.2s ease',
        '&:hover': {
            opacity: 1,
        },
        '& svg': {
            width: '16px',
            height: '16px',
        }
    },
    titleSection: {
        display: 'flex',
        justifyContent: 'space-between',
        paddingBottom: '24px',
    }
}));

const CustomerDetailsCard = ({ data, refreshCustomerView }) => {
    const classes = useStyles();
    const [copiedField, setCopiedField] = useState(null);
    const { id: customerId, name, email, sourceOfFunds, phone: phoneNumber, dateCreated, customerType } = data;
    const formattedCustomerType = formatCustomerType(customerType?.toUpperCase());
    const formattedDate = formatLongDate(dateCreated);
    const formattedAddress = data?.address?.formattedAddress || '-'

    const handleCopyToClipboard = async (text, fieldName) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedField(fieldName);
            setTimeout(() => setCopiedField(null), 2000);
        } catch (err) {
            console.error('Failed to copy text: ', err);
        }
    };

    const rowData = [
        {
            label: 'Name',
            value: name
        },
        {
            label: 'Customer type',
            value: formattedCustomerType
        },
        {
            label: 'Phone',
            value: phoneNumber
        },
        {
            label: 'Email',
            value: email,
            copyable: true
        },
        {
            label: 'Source of funds',
            value: formatSourceOfFunds(sourceOfFunds)
        },
        {
            label: 'Address',
            value: formattedAddress
        },
        {
            label: 'Customer ID',
            value: customerId,
            copyable: true
        },
        {
            label: 'Date joined',
            value: formattedDate
        },
    ];
    return (
        <Card elevation={0} className={classes.root}>
            <CardContent className={classes.content}>
                <div className={classes.titleSection}>
                    <Typography className={classes.header} variant="h3">Customer details</Typography>
                    <OpsRole>
                        <ModalConsumer>
                            {({ showModal }) => (
                                <ActionButton
                                    variant="secondary"
                                    size="md"
                                    onClick={() => showModal(UpdateCustomerDrawer, { data, onComplete: refreshCustomerView })}
                                >
                                    {"Edit details >"}
                                </ActionButton>
                            )}
                        </ModalConsumer>
                    </OpsRole>
                </div>
                <div className={classes.detailsSection}>
                    {rowData.map(({ value, label, copyable }, index) => (
                        <div className={classes.rowContainer} key={index}>
                            <Typography variant="h6">{label}</Typography>
                            {copyable ? (
                                <div className={classes.valueWithCopy}>
                                    <Typography variant="body1">{value}</Typography>
                                    <Tooltip 
                                        title={copiedField === label ? 'Copied!' : 'Copy'}
                                        arrow
                                        placement="top"
                                    >
                                        <button
                                            className={classes.copyButton}
                                            onClick={() => handleCopyToClipboard(value, label)}
                                        >
                                            <CopyIcon />
                                        </button>
                                    </Tooltip>
                                </div>
                            ) : (
                                <Typography variant="body1">{value}</Typography>
                            )}
                        </div>
                    ))}
                </div>
            </CardContent>
        </Card>
    );
};

export default CustomerDetailsCard;
